/**
 * The place of supply for a SALES transaction, and where it came from.
 *
 * WHY THIS IS IN `domain/` AND NOT IN EITHER CALLER
 * -------------------------------------------------
 * Two callers need this exact answer: the editor preview, which shows it, and
 * `issueSalesInvoice`, which writes it. If each called the resolver with its own
 * idea of the inputs, they would eventually disagree — and the disagreement is
 * the worst kind of bug in a billing application, because the screen would show
 * CGST + SGST while the saved invoice carried IGST, and BOTH documents would
 * reconcile internally. No report and no totals-only test would catch it.
 *
 * `core/` holds the rules; this module holds the one agreed reading of "which of
 * them applies to a sale". It sits in `domain/` because `data/` and `features/`
 * are both above it, and a helper that only one of them may import is a helper
 * the other will end up re-implementing.
 *
 * The precedence, unchanged and locked:
 *
 *   valid explicit override -> delivery state -> customer state -> UNRESOLVED
 *
 * The business state is NOT a fallback, and reverse charge is NOT a source.
 */
import { resolvePlaceOfSupply, type PlaceOfSupplyResolution } from '@core/gst/split';

/** The part of a customer this rule reads. A `Party` satisfies it structurally. */
export interface SalePlaceOfSupplyParty {
  /** The registered state, from the customer master. */
  readonly stateCode: string;
  /**
   * The DEFAULT delivery state, if the customer has one.
   *
   * A master default, never the answer on its own: a per-sale delivery address
   * overrides it, and the transaction takes a copy. That copy is what makes a
   * single consignment to another state correct without rewriting where a
   * customer "always" receives goods.
   */
  readonly shippingStateCode?: string;
}

export function resolveInvoicePlaceOfSupply(input: {
  readonly businessStateCode: string;
  readonly customer: SalePlaceOfSupplyParty | null;
  readonly overrideStateCode?: string;
}): PlaceOfSupplyResolution {
  return resolvePlaceOfSupply({
    businessStateCode: input.businessStateCode,
    // No customer, no customer state. NOT an error here: the editor must be able
    // to render the place-of-supply field before a customer has been chosen, and
    // whether a missing counterparty is a problem is the caller's decision.
    customerStateCode: input.customer?.stateCode ?? '',
    deliveryStateCode: input.customer?.shippingStateCode ?? null,
    overrideStateCode: input.overrideStateCode ?? null,
  });
}
